import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@services/api.js';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId) => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (data) setProfile(data);
    } catch (e) {}
  };

  // onAuthStateChange fires once immediately on subscribe with the current
  // session (in addition to firing on every later sign-in/out/refresh), so
  // it already covers the "get the initial session" case on its own —
  // the separate getSession().then() call used to trigger a duplicate
  // fetchProfile() on every mount, racing against this same callback.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
        if (session?.user) {
          fetchProfile(session.user.id);
          // Without this, every PostHog person is an anonymous device id
          // that can never be joined to a Supabase user (main.jsx's
          // posthog.init() fires before any session exists). Calling
          // identify() here also merges that session's prior anonymous
          // events into the now-identified person.
          if (typeof window !== 'undefined' && window.posthog) {
            window.posthog.identify(session.user.id, { email: session.user.email });
          }
        } else {
          setProfile(null);
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Only set loading false AFTER profile is fetched (or fails)
  useEffect(() => {
    if (profile !== null) setLoading(false);
  }, [profile]);

  const signUp = async ({ email, password, fullName }) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async ({ email, password }) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  };

  const updateProfile = async (updates) => {
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    const userId = currentUser?.id || user?.id;
    if (!userId) { console.warn('updateProfile called with no user'); return null; }
    const { data, error } = await supabase
      .from('profiles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', userId)
      .select()
      .single();
    if (error) { console.warn('updateProfile error:', error.message); throw error; }
    setProfile(data);
    return data;
  };

  const refreshProfile = async () => {
    if (user?.id) await fetchProfile(user.id);
  };

  return (
    <AuthContext.Provider value={{
      user, profile, loading,
      signUp, signIn, signOut, updateProfile, refreshProfile,
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
};

export default AuthContext;
